import "react-perfect-scrollbar/dist/css/styles.css";
import { PersistGate } from "redux-persist/integration/react";
import { Provider, useDispatch } from "react-redux";
import reduxStore, { AppDispatch } from "../src/redux/store";
import "./App.css";
import Main from "./pages/Main";
import "./i18n/i18n";
import "moment/locale/vi";
import Toast, { toggleMessage } from "./components/Toast/Toast";
import ConfirmModal from "./components/ConfirmModal/ConfirmModal";
import { useEffect, useState } from "react";
import { CountryLanguage, changeLanguage } from "./i18n/i18n";

import { messaging, requestPermission } from "./firebase";
import { onMessage } from "firebase/messaging";
import { incrementNotification } from "./redux/user/user.duck";
import Snackbar from "@mui/material/Snackbar";
import { EFireBaseType } from "./models/common/models.enum";
import { useLocation } from "react-router-dom";
import { ROUTE_PATH } from "./common/constants/app.constant";
import { now } from "moment";
import { Typography } from "@mui/material";
import ChatSidebar from "./pages/Chat/ChatSidebar";
import { authTokens } from "./services/services";
import { connectSignal } from "./services/api/signalr.Service";
import useVersionChecker from "./hooks/useVersionChecker";
import { APP_VERSION } from "./AppVersion";

const App = () => {
  useVersionChecker();

  const dispatch: AppDispatch = useDispatch();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const msgchannel = new BroadcastChannel("notifications");
  const reloadChannel = new BroadcastChannel("reload-board");
  const reloadTask = new BroadcastChannel("reload-task");
  const reloadNotification = new BroadcastChannel("reload-notification");
  const [isSignalReady, setSignalReady] = useState(false);

  useEffect(() => {
    requestPermission().then(result => {
      if (result && messaging) {
        onMessage(messaging, message => handleOnMessage(message));
      }
    });
    return () => {
      reloadChannel.close();
      reloadTask.close();
    };
  }, []);

  useEffect(() => {
    const connect = async () => {
      const token = await authTokens.getAccessToken();
      if (token) {
        await connectSignal(token); // Kết nối SignalR 1 lần sau khi login

        setSignalReady(true);
      }
    };

    connect();
  }, []);

  const getLanguage = async () => {
    const user = await authTokens.getUser();
    if (user)
      if (!user.language) {
        changeLanguage(CountryLanguage.VI);
      } else {
        changeLanguage(user.language as CountryLanguage);
      }
    return;
  };

  useEffect(() => {
    getLanguage();
  }, []);

  useEffect(() => {
    const savedVersion = localStorage.getItem("APP_VERSION");
    if (savedVersion !== APP_VERSION) {
      localStorage.setItem("APP_VERSION", APP_VERSION);
      window.location.reload(); // hoặc reload hard nếu cần
      // window.location.reload(true); // 1 số browser cũ
    }
  }, []);

  useEffect(() => {
    msgchannel.addEventListener("message", event =>
      handleOnMessage(event.data)
    );
    return () => {
      msgchannel.close();
    };
  }, []);

  const handleOnMessage = (message: any) => {
    const notificationTitle =
      message.data?.Title ?? "Bạn nhận được 1 thông báo mới";

    const hiddenNotification =
      message.data?.Type === EFireBaseType.UpdateBoard.toString();

    const hiddenCommentTask =
      message.data?.Type === EFireBaseType.CommentTask.toString();

    const hiddenReplyCommentTask =
      message.data?.Type === EFireBaseType.ReplyCommentTask.toString();

    if (hiddenNotification && location.pathname === ROUTE_PATH.PROJECTS) {
      reloadChannel.postMessage("reload");
    }
    if (
      (hiddenCommentTask && location.pathname === ROUTE_PATH.TASK) ||
      (hiddenReplyCommentTask && location.pathname === ROUTE_PATH.TASK)
    ) {
      reloadTask.postMessage("reload");
    }

    reloadNotification.postMessage("reload");

    if (!hiddenNotification) {
      // Show the Toast notification
      setMessage(notificationTitle);
      setOpen(true);
      // Increment the notification count
      dispatch(incrementNotification());
    }
  };

  return (
    <Provider store={reduxStore.store}>
      <PersistGate loading={null} persistor={reduxStore.persistor}>
        <Snackbar
          anchorOrigin={{
            vertical: "top",
            horizontal: "right"
          }}
          key={"toast" + now()}
          open={open}
          autoHideDuration={3000}
          onClose={() => setOpen(false)}
          message={
            <>
              <Typography>{"Thông báo"}</Typography>
              <div dangerouslySetInnerHTML={{ __html: message }} />
            </>
          }
        />
        <Toast />
        <Main />
        {location.pathname !== ROUTE_PATH.LOGIN && isSignalReady && (
          <ChatSidebar />
        )}
        <ConfirmModal />
      </PersistGate>
    </Provider>
  );
};

export default App;
